import React, { createContext, useCallback, useContext, useEffect, useState, useRef, useMemo } from 'react';
import io from 'socket.io-client';
import { App as CapacitorApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { SOCKET_URL } from '../config/endpoints';
import { useAuth } from './AuthContext';
import { roundClock } from '../game/clock';
import { createRestartReconnector, restartRetryMs } from '../utils/socketReconnect';
import { isNativePlatform } from '../utils/platform';
import type {
    GameInitPayload, GameSyncPayload, GameStartingPayload,
    GameLaunchingPayload, GameStartedPayload, GameCrashedPayload, BetPlacedPayload,
    CashoutDonePayload, BetSuccessPayload, CashoutSuccessPayload,
    ChainInfo, GamePhase, RoundSpotlight
} from '../types/game.types';

type SocketType = ReturnType<typeof io>;

interface Player {
    id: string;
    username?: string;
    balance: number;
    totalBets?: number;
    totalWins?: number;
    isDemo?: boolean;
}

export interface GameInfo {
    phase: GamePhase;
    phaseStartTime: number;
    currentGame: Record<string, unknown> | null;
    bets: BetPlacedPayload[];
    cashouts: CashoutDonePayload[];
    spotlight?: RoundSpotlight | null;
    history: { id: number; crashPoint: number }[];
}

interface ProvablyFairData {
    serverSeedHash: string | null;
    serverSeed: string | null;
    clientSeed: string | null;
    gameHash: string | null;
    crashPoint: number | null;
    gameNumber: number | null;
    chainInfo: ChainInfo | null;
}

interface MyBetState {
    isActive: boolean;
    amount: number;
    cashedOut: boolean;
    cashoutMultiplier: number | null;
}

export type DevSimulationScenario = 'lifecycle' | 'late-run';

export interface DevSimulationControls {
    available: boolean;
    active: boolean;
    enter: () => void;
    exit: () => void;
    newRound: () => void;
    setPhase: (phase: GamePhase) => void;
    setMultiplier: (multiplier: number) => void;
    triggerCrash: (crashPoint?: number) => void;
    addPlayer: (username: string, amount: number) => string | null;
    removePlayer: (playerId: string) => void;
    cashoutPlayer: (playerId: string, multiplier?: number) => void;
    clearPlayers: () => void;
    runScenario: (scenario: DevSimulationScenario) => void;
    stopScenario: () => void;
    scenarioRunning: boolean;
}

export type ConnectionState = 'online' | 'reconnecting' | 'server-restarting';

/** Which app is talking to us; recorded per session for retention analytics. */
export function detectPlatform(): 'ios' | 'android' | 'web' {
    const capacitor = (globalThis as any).Capacitor;
    if (capacitor?.getPlatform) {
        const platform = capacitor.getPlatform();
        if (platform === 'ios' || platform === 'android') return platform;
    }
    return 'web';
}

export interface FriendsState { code: string | null; members: number; pending: string | null; }
const noFriends: FriendsState = { code: null, members: 0, pending: null };
interface SocketContextType {
    friends: FriendsState;
    friendsError: string | null;
    changeFriends: (action: 'create' | 'join' | 'leave' | 'cancel', code?: string) => Promise<void>;
    socket: SocketType | null;
    player: Player | null;
    gameInfo: GameInfo | null;
    currentMultiplier: number;
    isConnected: boolean;
    connectionState: ConnectionState;
    connectionError: string | null;
    provablyFair: ProvablyFairData;
    myBet: MyBetState;
    devSimulation: DevSimulationControls;
}

const noop = () => {};
const defaultDevSimulation: DevSimulationControls = {
    available: false,
    active: false,
    enter: noop,
    exit: noop,
    newRound: noop,
    setPhase: noop,
    setMultiplier: noop,
    triggerCrash: noop,
    addPlayer: () => null,
    removePlayer: noop,
    cashoutPlayer: noop,
    clearPlayers: noop,
    runScenario: noop,
    stopScenario: noop,
    scenarioRunning: false,
};

const defaultMyBet: MyBetState = {
    isActive: false,
    amount: 0,
    cashedOut: false,
    cashoutMultiplier: null,
};

const defaultProvablyFair: ProvablyFairData = {
    serverSeedHash: null,
    serverSeed: null,
    clientSeed: null,
    gameHash: null,
    crashPoint: null,
    gameNumber: null,
    chainInfo: null,
};

const SocketContext = createContext<SocketContextType>({
    friends: noFriends, friendsError: null, changeFriends: async () => {},
    socket: null,
    player: null,
    gameInfo: null,
    currentMultiplier: 1.00,
    isConnected: false,
    connectionState: 'reconnecting',
    connectionError: null,
    provablyFair: defaultProvablyFair,
    myBet: defaultMyBet,
    devSimulation: defaultDevSimulation,
});

export const useSocket = () => useContext(SocketContext);

const DEV_SIMULATION_AVAILABLE = process.env.NODE_ENV === 'development' || process.env.REACT_APP_SHOW_DEV_TOOLS === 'true';

interface DevSimulationState {
    gameInfo: GameInfo;
    currentMultiplier: number;
}

const roundKey = (info: GameInfo | null | undefined): string | number | null => {
    const current = info?.currentGame as any;
    return current?.id ?? current?.gameNumber ?? null;
};

const roundNumber = (info: GameInfo | null | undefined): number | null => {
    const n = (info?.currentGame as any)?.gameNumber;
    return typeof n === 'number' ? n : null;
};

const isOlderGameNumber = (current: GameInfo | null | undefined, incoming: unknown): boolean => {
    const currentNumber = roundNumber(current);
    const incomingNumber = typeof incoming === 'number' ? incoming : null;
    return currentNumber !== null && incomingNumber !== null && incomingNumber < currentNumber;
};

const emptyGameInfo = (phase: GamePhase, phaseStartTime: number, currentGame: Record<string, unknown>): GameInfo => ({
    phase,
    phaseStartTime,
    currentGame,
    bets:     [],
    cashouts: [],
    history:  [],
});

const eventMatchesRound = (current: GameInfo | null | undefined, event: unknown): boolean => {
    const data = event as any;
    const currentGame = current?.currentGame as any;
    if (data?.gameId !== undefined && currentGame?.id !== undefined) {
        return currentGame.id === data.gameId;
    }
    if (data?.gameNumber !== undefined && currentGame?.gameNumber !== undefined) {
        return currentGame.gameNumber === data.gameNumber;
    }
    return true;
};

export const createSimulationGameInfo = (sequence: number, history: GameInfo['history'] = []): GameInfo => ({
    phase: 'STARTING',
    phaseStartTime: Date.now(),
    currentGame: {
        id: `local-simulation-${sequence}`,
        gameNumber: 900000 + sequence,
        serverSeedHash: `local-simulation-seed-${sequence}`,
        clientSeed: 'whiteout-dev-sandbox',
        startTime: null,
        endTime: null,
    },
    bets: [],
    cashouts: [],
    history,
});

export const simulationBet = (info: GameInfo, playerId: string, username: string, amount: number): BetPlacedPayload => ({
    playerId,
    username,
    amount,
    hasAutoCashout: false,
    gameId: (info.currentGame as any)?.id,
    gameNumber: (info.currentGame as any)?.gameNumber,
});

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { token, isAuthenticated, updateBalance, endSession } = useAuth();
    const [friends, setFriends] = useState<FriendsState>(noFriends);
    const [friendsError, setFriendsError] = useState<string | null>(null);
    const [socket, setSocket]                   = useState<SocketType | null>(null);
    const [player, setPlayer]                   = useState<Player | null>(null);
    const [gameInfo, setGameInfo]               = useState<GameInfo | null>(null);
    const [currentMultiplier, setCurrentMultiplier] = useState(1.00);
    // 'online' | 'reconnecting' | 'server-restarting' — drives the banner and
    // disables the bet controls while we cannot reach the round engine.
    const [connectionState, setConnectionState] = useState<ConnectionState>('reconnecting');
    // Kept in a ref so a reconnect can continue the same play session rather than
    // starting a new one for every tunnel.
    const sessionIdRef = useRef<string | null>(null);
    const [isConnected, setIsConnected]         = useState(false);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const [provablyFair, setProvablyFair]       = useState<ProvablyFairData>(defaultProvablyFair);
    const [devSimulationState, setDevSimulationState] = useState<DevSimulationState | null>(null);
    const [scenarioRunning, setScenarioRunning] = useState(false);
    const simulationSequenceRef = useRef(1);
    const scenarioTimersRef = useRef<number[]>([]);


    const playerRef = useRef<Player | null>(null);
    useEffect(() => { playerRef.current = player; }, [player]);

    const gameInfoRef = useRef<GameInfo | null>(null);
    useEffect(() => { gameInfoRef.current = gameInfo; }, [gameInfo]);

    const simulationActive = DEV_SIMULATION_AVAILABLE && devSimulationState !== null;
    const effectiveGameInfo = simulationActive ? devSimulationState.gameInfo : gameInfo;
    const effectiveMultiplier = simulationActive ? devSimulationState.currentMultiplier : currentMultiplier;

    const myBet = useMemo<MyBetState>(() => {
        if (!player?.id || !effectiveGameInfo) return defaultMyBet;
        const bet     = (effectiveGameInfo.bets     as any[]).find((b: any) => b.playerId === player.id);
        const cashout = (effectiveGameInfo.cashouts as any[]).find((c: any) => c.playerId === player.id);

        if (!bet) return defaultMyBet;

        return {
            isActive:          true,
            amount:            bet.amount ?? 0,
            cashedOut:         !!cashout,
            cashoutMultiplier: cashout?.multiplier ?? null,
        };
    }, [player?.id, effectiveGameInfo]);

    const stopScenario = useCallback(() => {
        for (const timer of scenarioTimersRef.current) window.clearTimeout(timer);
        scenarioTimersRef.current = [];
        setScenarioRunning(false);
    }, []);

    const enterSimulation = useCallback(() => {
        if (!DEV_SIMULATION_AVAILABLE) return;
        stopScenario();
        const sequence = simulationSequenceRef.current++;
        setDevSimulationState({
            gameInfo: createSimulationGameInfo(sequence, gameInfoRef.current?.history ?? []),
            currentMultiplier: 1,
        });
    }, [stopScenario]);

    const exitSimulation = useCallback(() => {
        stopScenario();
        setDevSimulationState(null);
    }, [stopScenario]);

    const newSimulationRound = useCallback(() => {
        if (!DEV_SIMULATION_AVAILABLE) return;
        stopScenario();
        setDevSimulationState(prev => ({
            gameInfo: createSimulationGameInfo(simulationSequenceRef.current++, prev?.gameInfo.history ?? gameInfoRef.current?.history ?? []),
            currentMultiplier: 1,
        }));
    }, [stopScenario]);

    const setSimulationPhase = useCallback((phase: GamePhase) => {
        setDevSimulationState(prev => {
            if (!prev) return prev;
            if (phase === 'STARTING') {
                return {
                    gameInfo: createSimulationGameInfo(simulationSequenceRef.current++, prev.gameInfo.history),
                    currentMultiplier: 1,
                };
            }
            return {
                ...prev,
                currentMultiplier: phase === 'LAUNCHING' ? 1 : prev.currentMultiplier,
                gameInfo: {
                    ...prev.gameInfo,
                    phase,
                    phaseStartTime: Date.now(),
                    currentGame: {
                        ...(prev.gameInfo.currentGame ?? {}),
                        startTime: phase === 'IN_PROGRESS' ? Date.now() : (prev.gameInfo.currentGame as any)?.startTime ?? null,
                        endTime: phase === 'CRASHED' ? Date.now() : null,
                    },
                },
            };
        });
    }, []);

    const setSimulationMultiplier = useCallback((multiplier: number) => {
        const safe = Number.isFinite(multiplier) ? Math.max(1, Math.min(1000000, multiplier)) : 1;
        setDevSimulationState(prev => prev ? { ...prev, currentMultiplier: safe } : prev);
    }, []);

    const triggerSimulationCrash = useCallback((crashPoint?: number) => {
        setDevSimulationState(prev => {
            if (!prev) return prev;
            const point = Number.isFinite(crashPoint) ? Math.max(1, Number(crashPoint)) : prev.currentMultiplier;
            return {
                ...prev,
                currentMultiplier: point,
                gameInfo: {
                    ...prev.gameInfo,
                    phase: 'CRASHED',
                    phaseStartTime: Date.now(),
                    currentGame: { ...(prev.gameInfo.currentGame ?? {}), endTime: Date.now() },
                },
            };
        });
    }, []);

    const addSimulationPlayer = useCallback((username: string, amount: number): string | null => {
        if (!devSimulationState) return null;
        const sequence = simulationSequenceRef.current++;
        const playerId = `LOCAL_${Date.now()}_${sequence}`;
        const safeName = (username.trim() || `LocalRider${sequence}`).slice(0, 20);
        const safeAmount = Number.isFinite(amount) ? Math.max(1, amount) : 100;
        setDevSimulationState(prev => {
            if (!prev) return prev;
            const bet = simulationBet(prev.gameInfo, playerId, safeName, safeAmount);
            return { ...prev, gameInfo: { ...prev.gameInfo, bets: [...prev.gameInfo.bets, bet] } };
        });
        return playerId;
    }, [devSimulationState]);

    const removeSimulationPlayer = useCallback((playerId: string) => {
        setDevSimulationState(prev => prev ? {
            ...prev,
            gameInfo: {
                ...prev.gameInfo,
                bets: prev.gameInfo.bets.filter(bet => bet.playerId !== playerId),
                cashouts: prev.gameInfo.cashouts.filter(cashout => cashout.playerId !== playerId),
            },
        } : prev);
    }, []);

    const cashoutSimulationPlayer = useCallback((playerId: string, multiplier?: number) => {
        setDevSimulationState(prev => {
            if (!prev) return prev;
            const bet = prev.gameInfo.bets.find(item => item.playerId === playerId);
            if (!bet) return prev;
            const point = Number.isFinite(multiplier) ? Math.max(1, Number(multiplier)) : prev.currentMultiplier;
            const cashout: CashoutDonePayload = {
                playerId,
                username: bet.username,
                gameId: (prev.gameInfo.currentGame as any)?.id,
                gameNumber: (prev.gameInfo.currentGame as any)?.gameNumber,
                multiplier: point,
                profit: bet.amount * (point - 1),
            };
            return {
                ...prev,
                gameInfo: {
                    ...prev.gameInfo,
                    cashouts: [...prev.gameInfo.cashouts.filter(item => item.playerId !== playerId), cashout],
                },
            };
        });
    }, []);

    const clearSimulationPlayers = useCallback(() => {
        setDevSimulationState(prev => prev ? {
            ...prev,
            gameInfo: { ...prev.gameInfo, bets: [], cashouts: [] },
        } : prev);
    }, []);

    const runSimulationScenario = useCallback((scenario: DevSimulationScenario) => {
        if (!DEV_SIMULATION_AVAILABLE) return;
        stopScenario();
        const info = createSimulationGameInfo(simulationSequenceRef.current++, gameInfoRef.current?.history ?? []);
        const riders = Array.from({ length: 5 }, (_, index) => simulationBet(info, `SCENARIO_${index + 1}`, `TestRider${index + 1}`, 50 + index * 25));
        if (scenario === 'late-run') {
            const cashouts: CashoutDonePayload[] = riders.slice(0, 2).map((bet, index) => ({
                playerId: bet.playerId,
                username: bet.username,
                gameId: bet.gameId,
                gameNumber: bet.gameNumber,
                multiplier: 1.5 + index * 1.25,
                profit: bet.amount * (.5 + index * 1.25),
            }));
            setDevSimulationState({
                currentMultiplier: 6.21,
                gameInfo: {
                    ...info,
                    phase: 'IN_PROGRESS',
                    phaseStartTime: Date.now() - 18000,
                    currentGame: { ...(info.currentGame ?? {}), startTime: Date.now() - 18000 },
                    bets: riders,
                    cashouts,
                },
            });
            return;
        }

        setScenarioRunning(true);
        setDevSimulationState({ ...{ currentMultiplier: 1 }, gameInfo: { ...info, bets: riders } });
        const schedule = (delay: number, callback: () => void) => {
            const timer = window.setTimeout(callback, delay);
            scenarioTimersRef.current.push(timer);
        };
        schedule(1800, () => setSimulationPhase('LAUNCHING'));
        schedule(3000, () => setSimulationPhase('IN_PROGRESS'));
        [1.25, 1.5, 2.5, 5, 10, 25].forEach((point, index) => schedule(3900 + index * 850, () => {
            setSimulationMultiplier(point);
            if (point === 2.5) cashoutSimulationPlayer('SCENARIO_1', point);
            if (point === 5) removeSimulationPlayer('SCENARIO_5');
        }));
        schedule(9400, () => {
            triggerSimulationCrash(25);
            setScenarioRunning(false);
            scenarioTimersRef.current = [];
        });
    }, [cashoutSimulationPlayer, removeSimulationPlayer, setSimulationMultiplier, setSimulationPhase, stopScenario, triggerSimulationCrash]);

    useEffect(() => stopScenario, [stopScenario]);
    useEffect(() => {
        if (isAuthenticated) return;
        stopScenario();
        setDevSimulationState(null);
    }, [isAuthenticated, stopScenario]);

    useEffect(() => {
        if (!isAuthenticated || !token) {
            setFriends(noFriends);
            setFriendsError(null);
            if (socket) {
                socket.disconnect();
                setSocket(null);
                setIsConnected(false);
                setPlayer(null);
                setGameInfo(null);
            }
            return;
        }

        const newSocket = io(SOCKET_URL, {
            auth: { token },
            // Mobile networks drop constantly. Giving up after five attempts left
            // the app permanently offline after about fifteen seconds in a tunnel;
            // it now keeps trying with a widening, jittered gap.
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 30000,
            randomizationFactor: 0.5,
        });

        setSocket(newSocket);

        // A server-side disconnect (deploy or restart) is never retried by
        // socket.io itself; this brings the socket back unless the session ended.
        let sessionEnded = false;
        const reconnector = createRestartReconnector({
            socket: {
                get connected() { return newSocket.connected; },
                // socket.io-client 4 exposes `active`; the bundled v1 typings do not.
                get active() { return Boolean((newSocket as unknown as { active?: boolean }).active); },
                connect: () => newSocket.connect(),
                disconnect: () => newSocket.disconnect(),
            },
            hasSessionEnded: () => sessionEnded,
        });
        let restartBannerTimer: number | undefined;
        const onVisibilityChange = () => { if (document.visibilityState === 'visible') reconnector.resume(); };
        document.addEventListener('visibilitychange', onVisibilityChange);
        let resumeHandle: PluginListenerHandle | null = null;
        let disposed = false;
        if (isNativePlatform()) {
            CapacitorApp.addListener('resume', () => reconnector.resume())
                .then(handle => { if (disposed) void handle.remove(); else resumeHandle = handle; })
                .catch(() => { /* visibilitychange still covers the return to the app */ });
        }

        newSocket.on('friends:state', (state: FriendsState) => { setFriends(state); setFriendsError(null); });
        newSocket.on('friends:error', (message: string) => setFriendsError(message));
        newSocket.on('session:ended', () => { sessionEnded = true; endSession(); });
        newSocket.on('connect', () => {
            reconnector.onConnect();
            setIsConnected(true);
            setConnectionError(null);
            setConnectionState('online');
            // Local round state is discarded on reconnect: the authoritative view
            // arrives in game:init, and merging the two would show a stale round.
            roundClock.reset();
            newSocket.emit('session:hello', {
                platform: detectPlatform(),
                appVersion: process.env.REACT_APP_VERSION || 'dev',
                locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
                resumeSessionId: sessionIdRef.current,
            });
        });

        newSocket.on('session:ack', (data: { sessionId: string }) => {
            sessionIdRef.current = data?.sessionId ?? null;
        });

        // Emitted while the server drains for a restart, so the UI can say what is
        // happening instead of just freezing.
        newSocket.on('server:restarting', (data: { retryAfterMs?: number }) => {
            reconnector.noteServerRestarting(data?.retryAfterMs);
            setConnectionState('server-restarting');
            setConnectionError(null);
            // While the round drains the socket is still up, so "Server updating"
            // stays; only a socket that is actually down turns into "Reconnecting".
            window.clearTimeout(restartBannerTimer);
            restartBannerTimer = window.setTimeout(() => setConnectionState(state => (
                state === 'server-restarting' && !newSocket.connected ? 'reconnecting' : state
            )), restartRetryMs(data?.retryAfterMs));
        });

        newSocket.on('connect_error', (error: Error) => {
            if ((error as Error & {data?: {code?: string}}).data?.code === 'SESSION_ENDED') { sessionEnded = true; endSession(); }
            setConnectionError(error.message);
            setIsConnected(false);
        });

        newSocket.on('disconnect', (reason: string) => {
            setIsConnected(false);
            setConnectionState(state => (state === 'server-restarting' ? state : 'reconnecting'));
            reconnector.onDisconnect(reason);
            console.log('Disconnected from server:', reason);
        });

        newSocket.on('error', (error: string) => {
            setConnectionError(error);
        });

        newSocket.on('game:init', (data: GameInitPayload & { betStatus?: any }) => {
            reconnector.onReady();
            setPlayer(data.player);

            let gi = data.gameInfo;

            const betStatus = data.betStatus;
            if (
                betStatus?.hasBet &&
                gi &&
                (gi.phase === 'IN_PROGRESS' || gi.phase === 'CRASHED') &&
                !(gi.bets as any[]).find((b: any) => b.playerId === data.player.id)
            ) {
                gi = {
                    ...gi,
                    bets: [
                        ...gi.bets,
                        {
                            playerId:       data.player.id,
                            username:       data.player.username ?? '',
                            chroma:         data.player.chroma,
                            gameId:         (gi.currentGame as any)?.id,
                            gameNumber:     (gi.currentGame as any)?.gameNumber,
                            amount:         betStatus.amount ?? 0,
                            hasAutoCashout: betStatus.hasAutoCashout ?? false,
                        } as BetPlacedPayload,
                    ],
                    cashouts: betStatus.hasCashedOut
                        ? [
                            ...gi.cashouts,
                            {
                                playerId:   data.player.id,
                                username:   data.player.username ?? '',
                                chroma:     data.player.chroma,
                                gameId:     (gi.currentGame as any)?.id,
                                gameNumber: (gi.currentGame as any)?.gameNumber,
                                multiplier: betStatus.cashoutMultiplier ?? 1,
                                profit:     betStatus.profit ?? 0,
                            } as CashoutDonePayload,
                          ]
                        : gi.cashouts,
                };
            }

            setGameInfo(gi);

            if (data.serverNow) roundClock.observeServerTime(data.serverNow);
            if (gi?.phase === 'IN_PROGRESS' && data.startTime) {
                // Joining a round already in progress: the start time is all the
                // client needs to draw the curve from the right place.
                roundClock.startRound(data.startTime, data.serverNow);
                if (data.currentMultiplier) roundClock.observeSync(data.currentMultiplier, data.serverNow ?? Date.now());
            } else if (gi?.phase === 'CRASHED') {
                roundClock.freeze((gi.currentGame as any)?.crashPoint ?? data.currentMultiplier ?? 1);
            } else {
                roundClock.reset();
            }
            setCurrentMultiplier(roundClock.multiplier());

            const current = gi?.currentGame as any;
            setProvablyFair({
                serverSeedHash: current?.serverSeedHash ?? null,
                serverSeed: gi?.phase === 'CRASHED' ? current?.serverSeed ?? null : null,
                clientSeed: current?.clientSeed ?? null,
                gameHash: gi?.phase === 'CRASHED' ? current?.gameHash ?? null : null,
                crashPoint: gi?.phase === 'CRASHED' ? current?.crashPoint ?? null : null,
                gameNumber: current?.gameNumber ?? null,
                chainInfo: current?.chainInfo ?? null,
            });
        });

        // The multiplier is a pure function of time, so the client computes every
        // frame itself and the server only corrects once a second. That removes
        // roughly 95% of the traffic and, just as importantly, stops a React
        // state update twenty times a second from re-rendering the whole tree.
        newSocket.on('game:sync', (data: GameSyncPayload) => {
            if (gameInfoRef.current?.phase !== 'IN_PROGRESS') return;
            roundClock.observeSync(data.multiplier, data.serverNow);
            setCurrentMultiplier(roundClock.multiplier());
        });

        newSocket.on('game:starting', (data: GameStartingPayload & { phaseStartTime?: number }) => {
            roundClock.reset();
            if (data.serverNow) roundClock.observeServerTime(data.serverNow);
            setGameInfo(prev => {
                if (isOlderGameNumber(prev, data.gameNumber)) return prev;
                const phaseStartTime = data.phaseStartTime ?? Date.now();
                const currentGame = {
                    ...(prev?.currentGame ?? {}),
                    id:             data.gameId,
                    gameNumber:     data.gameNumber,
                    serverSeedHash: data.serverSeedHash,
                    clientSeed:     data.clientSeed,
                    chainInfo:      data.chainInfo,
                    gameHash:       undefined,
                    serverSeed:     undefined,
                    crashPoint:     undefined,
                    startTime:      null,
                    endTime:        null,
                };
                if (!prev) return emptyGameInfo('STARTING', phaseStartTime, currentGame);
                return {
                    ...prev,
                    phase:          'STARTING',
                    phaseStartTime,
                    currentGame,
                bets:           [],
                cashouts:       [],
                };
            });
            setCurrentMultiplier(1.00);
            setProvablyFair({
                serverSeedHash: data.serverSeedHash || null,
                serverSeed:     null,
                clientSeed:     data.clientSeed    || null,
                gameHash:       null,
                crashPoint:     null,
                gameNumber:     data.gameNumber    || null,
                chainInfo:      data.chainInfo     || null,
            });
        });

        newSocket.on('game:started', (data: GameStartedPayload & { phaseStartTime?: number }) => {
            roundClock.startRound(data.startTime, data.serverNow);
            setCurrentMultiplier(1);
            setGameInfo(prev => {
                if (isOlderGameNumber(prev, data.gameNumber)) return prev;
                const phaseStartTime = data.phaseStartTime ?? Date.now();
                const currentGame = {
                    ...(prev?.currentGame ?? {}),
                    id:         data.gameId,
                    gameNumber: data.gameNumber,
                    startTime:  data.startTime,
                };
                if (!prev) return emptyGameInfo('IN_PROGRESS', phaseStartTime, currentGame);
                return { ...prev, phase: 'IN_PROGRESS', phaseStartTime, currentGame };
            });
        });

        newSocket.on('game:launching', (data: GameLaunchingPayload & { phaseStartTime?: number }) => {
            setGameInfo(prev => {
                if (isOlderGameNumber(prev, data.gameNumber)) return prev;
                const phaseStartTime = data.phaseStartTime ?? data.launchStartTime ?? Date.now();
                const currentGame = {
                    ...(prev?.currentGame ?? {}),
                    id:         data.gameId,
                    gameNumber: data.gameNumber,
                };
                if (!prev) return emptyGameInfo('LAUNCHING', phaseStartTime, currentGame);
                return { ...prev, phase: 'LAUNCHING', phaseStartTime, currentGame };
            });
            setCurrentMultiplier(1.00);
        });

        newSocket.on('game:crashed', (data: GameCrashedPayload) => {
            if (isOlderGameNumber(gameInfoRef.current, data.gameNumber)) return;
            // The server decides where the round ended; the local estimate stops here.
            roundClock.freeze(data.crashPoint);
            setGameInfo(prev => prev ? {
                ...prev,
                phase:   'CRASHED',
                spotlight: data.spotlight,
                history: (data as any).history ?? prev.history,
            } : null);
            setCurrentMultiplier(data.crashPoint);
            setProvablyFair(prev => ({
                ...prev,
                serverSeed:  data.serverSeed  || null,
                clientSeed:  data.clientSeed  || null,
                gameHash:    data.gameHash    || null,
                crashPoint:  data.crashPoint  || null,
                gameNumber:  data.gameNumber  || prev.gameNumber,
                chainInfo:   data.chainInfo   || prev.chainInfo,
            }));
        });

        newSocket.on('game:bet:placed', (data: BetPlacedPayload) => {
            setGameInfo(prev => {
                if (!prev) return null;
                if (!eventMatchesRound(prev, data)) return prev;
                if (prev.phase === 'CRASHED') return prev;
                const without = prev.bets.filter(
                    b => (b as any).playerId !== (data as any).playerId
                );
                const owned = data.playerId === playerRef.current?.id
                    ? prev.bets.find(b => b.playerId === data.playerId) : undefined;
                return { ...prev, bets: [...without, owned ? { ...owned, ...data } : data] };
            });
        });

        newSocket.on('game:cashout:done', (data: CashoutDonePayload) => {
            setGameInfo(prev => {
                if (!prev) return null;
                if (!eventMatchesRound(prev, data)) return prev;
                if (prev.phase !== 'IN_PROGRESS' && prev.phase !== 'CRASHED') return prev;
                const without = prev.cashouts.filter(
                    c => (c as any).playerId !== (data as any).playerId
                );
                return { ...prev, cashouts: [...without, data] };
            });
        });

        newSocket.on('game:bet:success', (data: BetSuccessPayload) => {
            setPlayer(prev => prev ? { ...prev, balance: data.newBalance } : null);
            updateBalance(data.newBalance);
            setGameInfo(prev => {
                const bet      = data.bet as any;
                const playerId = bet?.playerId ?? playerRef.current?.id;
                if (!prev || !playerId) return prev;
                if (!eventMatchesRound(prev, bet)) return prev;
                const normalizedBet = {
                    ...bet,
                    playerId,
                    username:       bet?.username ?? playerRef.current?.username ?? '',
                    amount:         bet?.amount ?? 0,
                    hasAutoCashout: bet?.hasAutoCashout ?? false,
                } as BetPlacedPayload;
                const without = prev.bets.filter(
                    b => (b as any).playerId !== playerId
                );
                return { ...prev, bets: [...without, normalizedBet] };
            });
        });

        newSocket.on('game:cashout:success', (data: CashoutSuccessPayload) => {
            setPlayer(prev => prev ? { ...prev, balance: data.newBalance } : null);
            updateBalance(data.newBalance);
        });

        newSocket.on('game:info:updated', (data: GameInfo) => {
            setGameInfo(prev => {
                if (isOlderGameNumber(prev, roundNumber(data))) return prev;

                const myId = playerRef.current?.id;
                const isSameRound = roundKey(prev) !== null && roundKey(prev) === roundKey(data);
                const mayPreservePrivateBet =
                    isSameRound;

                if (prev && myId && mayPreservePrivateBet) {
                    const myPrevBet     = prev.bets.find(b => (b as any).playerId === myId);
                    const myNewBet      = data.bets.find(b => (b as any).playerId === myId);
                    const myPrevCashout = prev.cashouts.find(c => (c as any).playerId === myId);
                    const myNewCashout  = data.cashouts.find(c => (c as any).playerId === myId);

                    const bets = myPrevBet
                        ? myNewBet
                            ? data.bets.map(b => b.playerId === myId ? { ...myPrevBet, ...b } : b)
                            : [...data.bets, myPrevBet]
                        : data.bets;
                    const cashouts = myPrevCashout && !myNewCashout ? [...data.cashouts, myPrevCashout] : data.cashouts;

                    if (bets !== data.bets || cashouts !== data.cashouts) {
                        return { ...data, bets, cashouts };
                    }
                }
                return data;
            });
        });

        return () => {
            disposed = true;
            reconnector.dispose();
            window.clearTimeout(restartBannerTimer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            if (resumeHandle) void resumeHandle.remove();
            newSocket.close();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, token, updateBalance, endSession]);

    const devSimulation = useMemo<DevSimulationControls>(() => ({
        available: DEV_SIMULATION_AVAILABLE,
        active: simulationActive,
        enter: enterSimulation,
        exit: exitSimulation,
        newRound: newSimulationRound,
        setPhase: setSimulationPhase,
        setMultiplier: setSimulationMultiplier,
        triggerCrash: triggerSimulationCrash,
        addPlayer: addSimulationPlayer,
        removePlayer: removeSimulationPlayer,
        cashoutPlayer: cashoutSimulationPlayer,
        clearPlayers: clearSimulationPlayers,
        runScenario: runSimulationScenario,
        stopScenario,
        scenarioRunning,
    }), [
        addSimulationPlayer, cashoutSimulationPlayer, clearSimulationPlayers,
        enterSimulation, exitSimulation, newSimulationRound, removeSimulationPlayer,
        runSimulationScenario, scenarioRunning, setSimulationMultiplier,
        setSimulationPhase, simulationActive, stopScenario, triggerSimulationCrash,
    ]);

    const changeFriends = useCallback(async (action: 'create' | 'join' | 'leave' | 'cancel', code?: string) => {
        if (!socket?.connected || simulationActive) throw new Error('Connect to the game before changing groups.');
        await new Promise<void>((resolve, reject) => {
            const timer = window.setTimeout(() => reject(new Error('No response yet. Reconnect to check your group before trying again.')), 7000);
            socket.emit('friends:change', { action, code }, (response: { ok: boolean; error?: string }) => {
                window.clearTimeout(timer);
                if (!response?.ok) reject(new Error(response?.error || 'Could not change group.'));
                else resolve();
            });
        });
    }, [socket, simulationActive]);

    const effectiveProvablyFair = simulationActive ? {
        ...defaultProvablyFair,
        serverSeedHash: (devSimulationState?.gameInfo.currentGame as any)?.serverSeedHash ?? 'local-simulation',
        clientSeed: 'whiteout-dev-sandbox',
        gameNumber: (devSimulationState?.gameInfo.currentGame as any)?.gameNumber ?? null,
        crashPoint: effectiveGameInfo?.phase === 'CRASHED' ? effectiveMultiplier : null,
    } : provablyFair;

    return (
        <SocketContext.Provider value={{
            socket: simulationActive ? null : socket,
            player,
            gameInfo: effectiveGameInfo,
            currentMultiplier: effectiveMultiplier,
            isConnected: simulationActive ? false : isConnected,
            connectionState: simulationActive ? 'online' : connectionState,
            connectionError: simulationActive ? null : connectionError,
            provablyFair: effectiveProvablyFair,
            myBet,
            friends, friendsError, changeFriends,
            devSimulation,
        }}>
            {children}
        </SocketContext.Provider>
    );
};
